import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import TimelineIcon from '@mui/icons-material/Timeline';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import pathfinderApi, { SavedDevice } from '../../services/pathfinderApi';
import AdtechDemographicsDialog from './AdtechDemographicsDialog';
import PromoteToPacketDialog, { PromoteTarget } from '@signals/app-shell/components/intel/PromoteToPacketDialog';

interface SavedDevicesPanelProps {
  courseId?: string;
  /** Currently selected trainee/entity ids (for "Save current selection"). */
  selectedStudents: string[];
  /** {id, name} used to label saved student devices and name-on-save. */
  students?: Array<{ id: string; name: string }>;
  adtechEnabled?: boolean;
  /** Select these trainee ids and load their data. */
  onLoadStudents: (studentIds: string[]) => void;
  /** Load full pattern-of-life for these AdTech registration ids. */
  onLoadAdtechPol?: (registrationIds: string[]) => void;
  /** True while an AdTech PoL load is in flight (disables PoL buttons). */
  adtechPolLoading?: boolean;
}

const SavedDevicesPanel: React.FC<SavedDevicesPanelProps> = ({
  courseId,
  selectedStudents,
  students = [],
  adtechEnabled = false,
  onLoadStudents,
  onLoadAdtechPol,
  adtechPolLoading = false,
}) => {
  const [devices, setDevices] = useState<SavedDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  // Per-device notes & tags: which row's editor is expanded + in-flight drafts.
  const [notesOpenId, setNotesOpenId] = useState<number | null>(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [tagsDraft, setTagsDraft] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  // Demographics/email enrichment dialog target registration ids.
  const [demoIds, setDemoIds] = useState<string[]>([]);
  const [addingAdtech, setAddingAdtech] = useState(false);
  const [adtechId, setAdtechId] = useState('');
  const [adtechName, setAdtechName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [promoteTarget, setPromoteTarget] = useState<PromoteTarget | null>(null);

  const nameFor = useMemo(() => {
    const list: Array<{ id: string; name: string }> = students || [];
    const m = new Map<string, string>();
    for (const s of list) m.set(s.id, s.name);
    return (id: string) => m.get(id) || id;
  }, [students]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDevices(await pathfinderApi.getSavedDevices(courseId));
      setError(null);
    } catch (e: any) {
      setError('Could not load saved devices');
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener('refresh-saved-devices', handler);
    return () => window.removeEventListener('refresh-saved-devices', handler);
  }, [load]);

  const saveSelection = useCallback(async () => {
    if (selectedStudents.length === 0) return;
    setBusy(true);
    try {
      await Promise.all(selectedStudents.map((id) =>
        pathfinderApi.saveDevice({
          registrationId: id,
          name: nameFor(id),
          deviceKind: 'student',
          courseId,
        }),
      ));
      await load();
    } catch {
      setError('Save failed');
    } finally {
      setBusy(false);
    }
  }, [selectedStudents, nameFor, courseId, load]);

  const addAdtech = useCallback(async () => {
    const id = adtechId.trim();
    if (!id) return;
    setBusy(true);
    try {
      await pathfinderApi.saveDevice({
        registrationId: id,
        name: adtechName.trim() || `AdTech ${id.slice(0, 8)}`,
        deviceKind: 'adtech',
      });
      setAdtechId('');
      setAdtechName('');
      setAddingAdtech(false);
      await load();
    } catch {
      setError('Save failed');
    } finally {
      setBusy(false);
    }
  }, [adtechId, adtechName, load]);

  const remove = useCallback(async (id: number) => {
    setBusy(true);
    try {
      await pathfinderApi.deleteSavedDevice(id);
      setChecked((prev) => { const n = new Set(prev); n.delete(id); return n; });
      await load();
    } finally {
      setBusy(false);
    }
  }, [load]);

  const commitRename = useCallback(async (id: number) => {
    const name = editName.trim();
    setEditingId(null);
    if (!name) return;
    try {
      await pathfinderApi.updateSavedDevice(id, { name });
      await load();
    } catch {
      setError('Rename failed');
    }
  }, [editName, load]);

  const toggleNotes = useCallback((d: SavedDevice) => {
    setNotesOpenId((prev) => {
      if (prev === d.id) return null;
      setNotesDraft(d.notes || '');
      setTagsDraft(Array.isArray(d.tags) ? d.tags : []);
      setTagInput('');
      return d.id;
    });
  }, []);

  const addTag = useCallback(() => {
    const t = tagInput.trim();
    if (!t) return;
    setTagsDraft((prev) => (prev.includes(t) ? prev : [...prev, t]));
    setTagInput('');
  }, [tagInput]);

  const removeTag = useCallback((tag: string) => {
    setTagsDraft((prev) => prev.filter((t) => t !== tag));
  }, []);

  const saveNotes = useCallback(async (id: number) => {
    setNotesSaving(true);
    try {
      // Persist notes + tags together; fold in any not-yet-added tag input.
      const pending = tagInput.trim();
      const tags = pending && !tagsDraft.includes(pending) ? [...tagsDraft, pending] : tagsDraft;
      await pathfinderApi.updateSavedDevice(id, { notes: notesDraft, tags });
      setNotesOpenId(null);
      setTagInput('');
      await load();
    } catch {
      setError('Saving notes failed');
    } finally {
      setNotesSaving(false);
    }
  }, [notesDraft, tagsDraft, tagInput, load]);

  const addToIntelPacket = useCallback((d: SavedDevice) => {
    const rid = d.registration_id.replace(/^GN-/i, '').trim();
    const kind = (d.device_kind || 'student') === 'adtech' ? 'adtech_id' : 'watchlist';
    setPromoteTarget({
      member_type: kind,
      member_id: rid,
      label: d.name || rid,
      is_primary: true,
      meta: { watchlist_id: d.id, notes: d.notes, tags: d.tags },
    });
  }, []);

  const toggleCheck = (id: number) => setChecked((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const selectedDeviceObjs = devices.filter((d) => checked.has(d.id));
  const studentIdsToLoad = Array.from(new Set(
    selectedDeviceObjs.filter((d) => (d.device_kind || 'student') !== 'adtech').map((d) => d.registration_id),
  ));
  const adtechIdsToLoad = Array.from(new Set(
    selectedDeviceObjs.filter((d) => (d.device_kind || '') === 'adtech').map((d) => d.registration_id),
  ));

  const isAdtech = (d: SavedDevice) => (d.device_kind || '') === 'adtech';

  return (
    <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <BookmarkIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
          Saved Devices ({devices.length})
        </Typography>
        {loading && <CircularProgress size={12} sx={{ ml: 0.5 }} />}
      </Box>

      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
        <Tooltip title="Save the currently selected trainees as devices">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<BookmarkAddIcon sx={{ fontSize: 14 }} />}
              disabled={busy || selectedStudents.length === 0}
              onClick={saveSelection}
              sx={{ fontSize: '0.65rem', textTransform: 'none', py: 0.25 }}
            >
              Save selection ({selectedStudents.length})
            </Button>
          </span>
        </Tooltip>
        {adtechEnabled && (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={() => setAddingAdtech((v) => !v)}
            sx={{ fontSize: '0.65rem', textTransform: 'none', py: 0.25 }}
          >
            AdTech ID
          </Button>
        )}
      </Box>

      {addingAdtech && (
        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, alignItems: 'center' }}>
          <TextField
            size="small" placeholder="Registration ID" value={adtechId}
            onChange={(e) => setAdtechId(e.target.value)}
            sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.5 } }}
          />
          <TextField
            size="small" placeholder="Name" value={adtechName}
            onChange={(e) => setAdtechName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addAdtech(); }}
            sx={{ width: 90, '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.5 } }}
          />
          <IconButton size="small" onClick={addAdtech} disabled={busy || !adtechId.trim()}>
            <CheckIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}

      {studentIdsToLoad.length > 0 && (
        <Button
          size="small"
          variant="contained"
          fullWidth
          disabled={busy}
          onClick={() => onLoadStudents(studentIdsToLoad)}
          sx={{ fontSize: '0.65rem', textTransform: 'none', py: 0.25, mb: 0.5 }}
        >
          Load {studentIdsToLoad.length} selected
        </Button>
      )}

      {adtechIdsToLoad.length > 0 && onLoadAdtechPol && (
        <Button
          size="small"
          variant="contained"
          color="secondary"
          fullWidth
          disabled={busy || adtechPolLoading}
          startIcon={adtechPolLoading ? <CircularProgress size={12} color="inherit" /> : <TimelineIcon sx={{ fontSize: 14 }} />}
          onClick={() => onLoadAdtechPol(adtechIdsToLoad)}
          sx={{ fontSize: '0.65rem', textTransform: 'none', py: 0.25, mb: 0.5 }}
        >
          {adtechPolLoading ? 'Loading PoL…' : `Load PoL for ${adtechIdsToLoad.length} AdTech device${adtechIdsToLoad.length > 1 ? 's' : ''}`}
        </Button>
      )}

      {adtechIdsToLoad.length > 0 && adtechEnabled && (
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          fullWidth
          startIcon={<PersonSearchIcon sx={{ fontSize: 14 }} />}
          onClick={() => setDemoIds(adtechIdsToLoad)}
          sx={{ fontSize: '0.65rem', textTransform: 'none', py: 0.25, mb: 0.5 }}
        >
          Enrich {adtechIdsToLoad.length} (demographics)
        </Button>
      )}

      {error && <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.5 }}>{error}</Typography>}

      {devices.length === 0 && !loading && (
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No saved devices yet. Select trainees and click “Save selection”.
        </Typography>
      )}

      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
        {devices.map((d) => (
          <Box key={d.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, py: 0.1 }}>
              <Checkbox
                size="small"
                checked={checked.has(d.id)}
                onChange={() => toggleCheck(d.id)}
                sx={{ p: 0.25 }}
              />
              {editingId === d.id ? (
                <>
                  <TextField
                    size="small" value={editName} autoFocus
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitRename(d.id);
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.4 } }}
                  />
                  <IconButton size="small" onClick={() => commitRename(d.id)}><CheckIcon sx={{ fontSize: 14 }} /></IconButton>
                  <IconButton size="small" onClick={() => setEditingId(null)}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
                </>
              ) : (
                <>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 500 }}>
                      {d.name || d.registration_id}
                    </Typography>
                  </Box>
                  <Chip
                    label={isAdtech(d) ? 'AdTech' : (d.device_type || 'Trainee')}
                    size="small"
                    sx={{ height: 15, fontSize: '0.55rem',
                      bgcolor: isAdtech(d) ? 'rgba(156,39,176,0.15)' : 'rgba(33,150,243,0.12)' }}
                  />
                  {isAdtech(d) ? (
                    <>
                      {onLoadAdtechPol && (
                        <Tooltip title="Load pattern of life for the selected date range">
                          <span>
                            <IconButton
                              size="small"
                              disabled={adtechPolLoading}
                              onClick={() => onLoadAdtechPol([d.registration_id])}
                            >
                              <TimelineIcon sx={{ fontSize: 13, color: adtechPolLoading ? 'text.disabled' : 'secondary.main' }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Enrich (demographics + hashed email)">
                        <IconButton size="small" onClick={() => setDemoIds([d.registration_id])}>
                          <PersonSearchIcon sx={{ fontSize: 13, color: 'secondary.main' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy registration ID">
                        <IconButton size="small" onClick={() => navigator.clipboard?.writeText(d.registration_id)}>
                          <ContentCopyIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <Tooltip title="Load this device">
                      <IconButton size="small" onClick={() => onLoadStudents([d.registration_id])}>
                        <BookmarkIcon sx={{ fontSize: 13, color: 'primary.main' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={(d.notes || (d.tags && d.tags.length)) ? 'View / edit notes & tags' : 'Add notes & tags'}>
                    <IconButton size="small" onClick={() => toggleNotes(d)}>
                      {(d.notes || (d.tags && d.tags.length))
                        ? <StickyNote2Icon sx={{ fontSize: 13, color: 'warning.main' }} />
                        : <StickyNote2OutlinedIcon sx={{ fontSize: 13 }} />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Promote to Intel Packet">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => addToIntelPacket(d)}
                      >
                        <TravelExploreIcon sx={{ fontSize: 13, color: 'info.main' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Rename">
                    <IconButton size="small" onClick={() => { setEditingId(d.id); setEditName(d.name || ''); }}>
                      <EditIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => remove(d.id)}>
                      <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>

            {/* Compact tag preview so labels are visible without opening editor. */}
            {notesOpenId !== d.id && Array.isArray(d.tags) && d.tags.length > 0 && (
              <Box sx={{ pl: 3.5, pr: 0.5, pb: 0.25, display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
                {d.tags.map((t) => (
                  <Chip key={t} label={t} size="small" sx={{ height: 14, fontSize: '0.5rem', bgcolor: 'rgba(255,255,255,0.06)' }} />
                ))}
              </Box>
            )}

            {/* Inline notes + tags editor (persisted on the watchlist row itself). */}
            <Collapse in={notesOpenId === d.id} unmountOnExit>
              <Box sx={{ pl: 3.5, pr: 0.5, pb: 0.75 }}>
                {/* Tag chips (add / remove) — persisted alongside notes. */}
                <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap', mb: 0.5, alignItems: 'center' }}>
                  {tagsDraft.map((t) => (
                    <Chip
                      key={t}
                      label={t}
                      size="small"
                      onDelete={() => removeTag(t)}
                      sx={{ height: 18, fontSize: '0.6rem' }}
                    />
                  ))}
                  <TextField
                    size="small"
                    placeholder="+ tag"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addTag(); }
                    }}
                    sx={{ width: 80, '& .MuiInputBase-input': { fontSize: '0.62rem', py: 0.25 } }}
                  />
                </Box>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={6}
                  placeholder="Notes for this device…"
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.7rem' } }}
                />
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    onClick={() => setNotesOpenId(null)}
                    sx={{ fontSize: '0.6rem', textTransform: 'none', py: 0 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={notesSaving}
                    onClick={() => saveNotes(d.id)}
                    sx={{ fontSize: '0.6rem', textTransform: 'none', py: 0 }}
                  >
                    {notesSaving ? 'Saving…' : 'Save notes'}
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </Box>
        ))}
      </Box>

      <AdtechDemographicsDialog
        open={demoIds.length > 0}
        registrationIds={demoIds}
        onClose={() => setDemoIds([])}
      />
      <PromoteToPacketDialog
        open={!!promoteTarget}
        onClose={() => setPromoteTarget(null)}
        target={promoteTarget}
        defaultName={promoteTarget?.label ? `Watchlist: ${promoteTarget.label}` : undefined}
      />
    </Box>
  );
};

export default SavedDevicesPanel;
